import { Checkbox } from '@spark-web/checkbox';
import { Field } from '@spark-web/field';
import { CheckIcon } from '@spark-web/icon';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { InputAdornment, TextInput } from '@spark-web/text-input';
import { TextLink } from '@spark-web/text-link';
import { ChangeEventHandler, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useOutletContext } from 'react-router';

import { MaskedTextInput } from '~/components/ui/masked-text-input';
import { PARTNER_CUSTOMER_ID_FIELD_CONFIG, PARTNER_REQUIREMENTS, RULE_EMAIL_PATTERN } from '~/constants';
import { useDebounce } from '~/hooks/useDebounce';
import { CustomerFormValues, MarketplacePartner, OutletContextType } from '~/types/marketplace';

import { AddressSuggestionInput } from './address-input';
import { MobileVerificationInput } from './mobile-verify-input';

const PARTNER_CUSTOMER_ID_FIELD_DEBOUNCE = 500;

type CustomerFormFieldProps = {
  leadType: 'Marketplace' | 'Concierge';
  excludedFields?: (keyof CustomerFormValues)[];
  onPartnerCustomerIdValidating?: (isValidating: boolean) => void;
};

export const CustomerFormField = ({
  leadType,
  excludedFields = [],
  onPartnerCustomerIdValidating,
}: CustomerFormFieldProps) => {
  const { categories, partner } = useOutletContext<OutletContextType>();
  const { control, watch, register, setValue, trigger } = useFormContext<CustomerFormValues>();

  const partnerRequirements = PARTNER_REQUIREMENTS[partner as MarketplacePartner];

  const customerIdFieldConfig = PARTNER_CUSTOMER_ID_FIELD_CONFIG[partner as MarketplacePartner];
  const customerIdFieldLabel = `${customerIdFieldConfig?.label || 'Customer ID'}${partnerRequirements?.hasCustomerIdField === 'optional' ? ' (optional)' : ''}`;

  const [isPartnerCustomerIdValidating, setIsPartnerCustomerIdValidating] = useState(false);

  const isPartnerCustomerIdVerified = watch('isPartnerCustomerIdVerified');
  const partnerCustomerId = watch('partnerCustomerId');
  const [debouncedPartnerCustomerId] = useDebounce(partnerCustomerId || '', PARTNER_CUSTOMER_ID_FIELD_DEBOUNCE);

  const onPartnerCustomerIdChange = (
    onChangeHandler: ChangeEventHandler<HTMLInputElement>,
  ): ChangeEventHandler<HTMLInputElement> => {
    return (e) => {
      onChangeHandler(e);
      // Clear previous verification state immediately when user types
      setValue('isPartnerCustomerIdVerified', undefined);
    };
  };

  // Use debounced value for validation to avoid excessive API calls
  useEffect(() => {
    if (debouncedPartnerCustomerId && customerIdFieldConfig?.validation) {
      const validateCustomerId = async () => {
        setIsPartnerCustomerIdValidating(true);
        const isFormatValid = await customerIdFieldConfig.validation?.(debouncedPartnerCustomerId);
        setValue('isPartnerCustomerIdVerified', isFormatValid === null ? undefined : isFormatValid);
        setIsPartnerCustomerIdValidating(false);
        // Trigger re-validation to update form validity state
        trigger('partnerCustomerId');
      };
      validateCustomerId();
    } else if (!debouncedPartnerCustomerId) {
      // Reset verification state when field is empty
      setValue('isPartnerCustomerIdVerified', undefined);
      // Trigger re-validation when field is cleared
      trigger('partnerCustomerId');
    }
  }, [debouncedPartnerCustomerId, customerIdFieldConfig, setValue, trigger]);

  useEffect(() => {
    if (onPartnerCustomerIdValidating) {
      onPartnerCustomerIdValidating(isPartnerCustomerIdValidating);
    }
  }, [isPartnerCustomerIdValidating, onPartnerCustomerIdValidating]);

  // Trigger re-validation when verification state changes to update form validity
  useEffect(() => {
    if (partnerRequirements?.hasCustomerIdField && partnerCustomerId) {
      trigger('partnerCustomerId');
    }
  }, [isPartnerCustomerIdVerified, trigger, partnerRequirements?.hasCustomerIdField, partnerCustomerId]);

  return (
    <Stack gap="xlarge">
      <Controller
        control={control}
        rules={{ required: true }}
        name="firstName"
        render={({ field: { onChange, value, onBlur, name }, fieldState }) => (
          <Field
            label="First name"
            tone={fieldState?.error ? 'critical' : 'neutral'}
            message={fieldState?.error?.message}
          >
            <TextInput name={name} value={value ?? ''} onBlur={onBlur} onChange={onChange} />
          </Field>
        )}
      />
      <Controller
        control={control}
        rules={{ required: true }}
        name="lastName"
        render={({ field: { onChange, value, onBlur, name }, fieldState }) => (
          <Field
            label="Last name"
            tone={fieldState?.error ? 'critical' : 'neutral'}
            message={fieldState?.error?.message}
          >
            <TextInput name={name} value={value ?? ''} onBlur={onBlur} onChange={onChange} />
          </Field>
        )}
      />

      {!excludedFields.includes('address') ? (
        <AddressSuggestionInput helperText="This helps provide you with an accurate quote." />
      ) : null}

      <MobileVerificationInput />

      <Controller
        control={control}
        rules={{ required: true, ...RULE_EMAIL_PATTERN }}
        name="email"
        render={({ field: { onChange, value, onBlur, name }, fieldState }) => (
          <Field label="Email" tone={fieldState?.error ? 'critical' : 'neutral'} message={fieldState?.error?.message}>
            <TextInput name={name} value={value ?? ''} onBlur={onBlur} onChange={onChange} />
            <Text size="xsmall">Brighte will keep you updated via email</Text>
          </Field>
        )}
      />

      {partnerRequirements?.hasCustomerIdField ? (
        <Controller
          control={control}
          rules={{
            required: partnerRequirements?.hasCustomerIdField === 'required',
            validate: (value) => {
              const isRequired = partnerRequirements?.hasCustomerIdField === 'required';
              const hasValidationConfig = !!customerIdFieldConfig?.validation;

              // If field is required but empty
              if (isRequired && !value) {
                return 'This field is required.';
              }

              // If no value provided and not required, it's valid
              if (!value && !isRequired) return true;

              // If there's a value but no validation config, accept it
              if (value && !hasValidationConfig) return true;

              // If there's a value with validation config but verification is still pending
              if (value && hasValidationConfig && isPartnerCustomerIdVerified === undefined) {
                return true; // Considered valid while validating; we'll block submission at button
              }

              // If there's a value and verification failed, it's invalid
              if (value && hasValidationConfig && isPartnerCustomerIdVerified === false) {
                return 'Invalid input. Please check your membership number and try again.';
              }

              // If required field has value and passed validation (or no validation needed)
              return true;
            },
          }}
          name="partnerCustomerId"
          render={({ field: { name, value, onChange }, fieldState }) => {
            return (
              <Field
                label={customerIdFieldLabel}
                tone={fieldState?.error ? 'critical' : 'neutral'}
                message={fieldState?.error?.message}
              >
                {customerIdFieldConfig?.maskitoOptions ? (
                  <MaskedTextInput
                    name={name}
                    value={value ?? ''}
                    placeholder={customerIdFieldConfig?.placeholder}
                    onChange={onPartnerCustomerIdChange(onChange)}
                    maskitoOptions={customerIdFieldConfig.maskitoOptions}
                  >
                    {customerIdFieldConfig?.icon ? (
                      <InputAdornment placement="start">{customerIdFieldConfig.icon()}</InputAdornment>
                    ) : null}
                    {customerIdFieldConfig.validation && isPartnerCustomerIdVerified === true ? (
                      <InputAdornment placement="end">
                        <CheckIcon tone="positive" size="xsmall" />
                      </InputAdornment>
                    ) : null}
                  </MaskedTextInput>
                ) : (
                  <TextInput
                    name={name}
                    value={value ?? ''}
                    placeholder={customerIdFieldConfig?.placeholder}
                    onChange={onPartnerCustomerIdChange(onChange)}
                  >
                    {customerIdFieldConfig?.icon ? (
                      <InputAdornment placement="start">{customerIdFieldConfig.icon()}</InputAdornment>
                    ) : null}
                    {customerIdFieldConfig?.validation && isPartnerCustomerIdVerified === true ? (
                      <InputAdornment placement="end">
                        <CheckIcon tone="positive" size="xsmall" />
                      </InputAdornment>
                    ) : null}
                  </TextInput>
                )}
                {customerIdFieldConfig?.description ? (
                  <Text size="xsmall">{customerIdFieldConfig.description}</Text>
                ) : null}
              </Field>
            );
          }}
        />
      ) : null}

      <Controller
        control={control}
        rules={{ required: true }}
        name="termsAndConditions"
        render={({ field: { onChange, value, onBlur, name }, fieldState }) => (
          <Field
            label="termsAndConditions"
            labelVisibility="hidden"
            tone={fieldState?.error ? 'critical' : 'neutral'}
            message={fieldState?.error?.message}
          >
            <Checkbox onChange={onChange} checked={value} onBlur={onBlur} name={name}>
              <Text size="xsmall">
                I consent to the use and disclosure of my contact details with Brighte, in accordance with{' '}
                <TextLink
                  target="_blank"
                  href="https://brighte.com.au/support/privacy"
                  tone="info"
                >{`Brighte's Privacy Policy`}</TextLink>
                {'. '}Brighte may receive a referral fee for referring your details.
              </Text>
            </Checkbox>
          </Field>
        )}
      />

      {partnerRequirements?.hasMarketingOptIn ? (
        <Controller
          control={control}
          name="partnerMarketingConsent"
          defaultValue={false}
          render={({ field: { onChange, value, onBlur, name }, fieldState }) => (
            <Field
              label="partnerMarketingConsent"
              labelVisibility="hidden"
              tone={fieldState?.error ? 'critical' : 'neutral'}
              message={fieldState?.error?.message}
              secondaryLabel="(Optional)"
            >
              <Checkbox onChange={onChange} checked={value} onBlur={onBlur} name={name}>
                <Text size="xsmall">
                  I want to receive marketing emails from {partnerRequirements?.name} (optional)
                </Text>
              </Checkbox>
            </Field>
          )}
        />
      ) : null}

      <input type="hidden" value={leadType} {...register('leadType')} />
      {categories ? <input type="hidden" value={categories} {...register('categories')} /> : null}
    </Stack>
  );
};
